import React, { useCallback, useEffect, useState } from 'react';
import { formatApiError } from '@/lib/api';
import { restaurantService } from '@/services/restaurant.service';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Star, Loader2, MessageSquare, ShieldCheck, PenLine } from 'lucide-react';
import { toast } from 'sonner';

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

function StarRow({ value = 0, size = 'h-4 w-4' }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`${size} ${n <= Math.round(value) ? 'fill-[hsl(var(--brand-sun))] text-[hsl(var(--brand-sun))]' : 'text-muted-foreground/40'}`} />
      ))}
    </span>
  );
}

function StarInput({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid="review-rating-input">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(n)}
          className="transition-transform hover:scale-110"
          aria-label={`${n} stelle`}
          data-testid={`review-star-${n}`}
        >
          <Star className={`h-7 w-7 ${n <= (hover || value) ? 'fill-[hsl(var(--brand-sun))] text-[hsl(var(--brand-sun))]' : 'text-muted-foreground/40'}`} />
        </button>
      ))}
    </div>
  );
}

export function RestaurantPublicReviews({ ownerId, restaurantName }) {
  const [data, setData] = useState({ items: [], average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ guest_name: '', rating: 0, comment: '', reservation_code: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await restaurantService.publicReviews(ownerId)); }
    catch (e) { /* silenzioso */ }
    finally { setLoading(false); }
  }, [ownerId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.guest_name.trim()) return toast.error('Inserisci il tuo nome');
    if (!form.rating) return toast.error('Assegna una valutazione');
    setSubmitting(true);
    try {
      const r = await restaurantService.publicSubmitReview(ownerId, form);
      const review = r?.review;
      if (review) setData((d) => ({ items: [review, ...d.items], count: d.count + 1, average: Number((((d.average * d.count) + review.rating) / (d.count + 1)).toFixed(1)) }));
      setSent(true);
      setForm({ guest_name: '', rating: 0, comment: '', reservation_code: '' });
      toast.success('Grazie per la tua recensione!');
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <section className="sila-section py-10" data-testid="restaurant-public-reviews">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_400px]">
        {/* Elenco recensioni */}
        <div>
          <div className="mb-5 flex items-center gap-3">
            <h2 className="font-display text-2xl font-bold">Recensioni</h2>
            {data.count > 0 && (
              <div className="flex items-center gap-2">
                <span className="font-display text-2xl font-bold text-[hsl(var(--brand-bark))]" data-testid="public-reviews-average">{Number(data.average || 0).toFixed(1)}</span>
                <div className="flex flex-col leading-none">
                  <StarRow value={data.average} />
                  <span className="mt-0.5 text-xs text-muted-foreground">{data.count} recensioni</span>
                </div>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : data.items.length === 0 ? (
            <Card className="p-8 text-center" data-testid="public-reviews-empty">
              <Star className="mx-auto mb-2 h-7 w-7 text-muted-foreground" />
              <p className="font-medium">Ancora nessuna recensione</p>
              <p className="text-sm text-muted-foreground">Sii il primo a lasciare la tua opinione{restaurantName ? ` su ${restaurantName}` : ''}.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {data.items.map((r) => (
                <Card key={r.id} className="p-4" data-testid={`public-review-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                        {(r.guest_name || 'C').charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <p className="flex items-center gap-1.5 text-sm font-semibold">
                          {r.guest_name}
                          {r.verified && <Badge variant="outline" className="gap-1 border-primary/20 bg-primary/10 py-0 text-[10px] text-primary"><ShieldCheck className="h-3 w-3" />Verificato</Badge>}
                        </p>
                        <p className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</p>
                      </div>
                    </div>
                    <StarRow value={r.rating} />
                  </div>
                  {r.comment && <p className="mt-2.5 text-sm leading-relaxed text-foreground/90">&ldquo;{r.comment}&rdquo;</p>}
                  {r.reply && (
                    <div className="mt-3 rounded-xl border border-border bg-muted/40 p-3">
                      <p className="flex items-center gap-1.5 text-xs font-semibold text-foreground"><MessageSquare className="h-3.5 w-3.5 text-primary" />Risposta di {restaurantName || 'del ristorante'}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{r.reply}</p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Form: lascia una recensione */}
        <div>
          <Card className="p-5 lg:sticky lg:top-24" data-testid="review-form-card">
            <h3 className="flex items-center gap-2 font-display text-lg font-bold"><PenLine className="h-5 w-5 text-primary" />Lascia una recensione</h3>
            {sent ? (
              <div className="py-6 text-center" data-testid="review-form-success">
                <ShieldCheck className="mx-auto h-10 w-10 text-primary" />
                <p className="mt-2 font-semibold">Grazie!</p>
                <p className="text-sm text-muted-foreground">La tua recensione è stata pubblicata.</p>
                <Button variant="outline" className="mt-3" onClick={() => setSent(false)}>Scrivi un'altra recensione</Button>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                <div>
                  <Label>La tua valutazione</Label>
                  <div className="mt-1"><StarInput value={form.rating} onChange={(v) => setForm({ ...form, rating: v })} /></div>
                </div>
                <div><Label>Nome</Label><Input value={form.guest_name} onChange={(e) => setForm({ ...form, guest_name: e.target.value })} placeholder="Il tuo nome" data-testid="review-name-input" /></div>
                <div><Label>Commento (facoltativo)</Label><Textarea rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Com'è andata la tua esperienza?" data-testid="review-comment-input" /></div>
                <div>
                  <Label>Codice prenotazione (facoltativo)</Label>
                  <Input value={form.reservation_code} onChange={(e) => setForm({ ...form, reservation_code: e.target.value })} placeholder="Es. RS-1234 — per il badge Verificato" data-testid="review-code-input" />
                </div>
                <Button className="w-full" onClick={submit} disabled={submitting} data-testid="review-submit-btn">
                  {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Pubblica recensione
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}

export default RestaurantPublicReviews;
